import { mdiContentCopy } from "@mdi/js";
import { Database, PenBoxIcon, TrashIcon } from 'lucide-react';
import { useCallback, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppAgentContext } from "src/context/ContextProvider";
import useIgnoreRenderEffect from "src/hooks/useIgnoreRenderEffect";
import { getAllAgentsService } from "src/services/AgentService";
import { appActions, DataStates, State } from "src/store";
import { AiAgent } from "src/types/AiAgent";
import { DateUtil, imgSource, imgVectorStore, validatePermissionModule } from 'src/utils';
import { LoadingComponent } from "../../shared/LoadingComponent";
import TableComponent, { KeyHeader } from "../../shared/TableComponent";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "../../ui/breadcrumb";
import { Permissions, PermissionType } from '../../../types/Permission';

export const AgentListPageComponent = () => {

    const {
        dispatch,
        state: { loading: { [DataStates.AGENT]: loadingAgents}, session: {permissions_list} },
    } = useContext(AppAgentContext);

    const navigate = useNavigate();

    const [totalItems, setTotalItems] = useState<AiAgent[]>([]);

    const validateAgentByPermissionType = ({agent, permissionType}:{
        agent: AiAgent, permissionType: PermissionType
    }) => {
        const keys = validatePermissionModule({
            permissions_list,
            module: Permissions.SBX_STUDIO_AGENT,
            types: [permissionType],
        });

        if (!agent.id){
            return false
        }
        return keys.length === 0 || keys.includes(agent.id);
    }

    const startingData = useCallback(async () => {
        dispatch(appActions.onLoading({ type: DataStates.AGENT, status: State.LOADING }));
        const res = await getAllAgentsService();
        if (res.success) {
            const arrayItems = res.items.filter(agent =>{
                return validateAgentByPermissionType({agent, permissionType: PermissionType.READ})
            }).sort((a, b) => {
                const date1 = new Date(a.updated_at);
                const date2 = new Date(b.updated_at);
                return date2.getTime() - date1.getTime();
            });
            // dispatch(appActions.setEmailTemplates(arrayItems));
            setTotalItems(arrayItems);
            dispatch(appActions.onLoading({ type: DataStates.AGENT, status: State.SUCCESS }));
        } else {
            dispatch(appActions.onLoading({ type: DataStates.AGENT, status: State.ERROR }));
        }
    }, []);

    useIgnoreRenderEffect(async () => {
        await startingData();
    }, [startingData]);

    if(loadingAgents === State.LOADING) {
        return <LoadingComponent />
    }
    
    return (
        <div className="mt-5 px-2">
            <div className="mb-5">
                <Breadcrumb>
                    <BreadcrumbList>
                        <BreadcrumbItem>
                            <BreadcrumbLink href="/">Inicio</BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbPage>Agentes</BreadcrumbPage>
                        </BreadcrumbItem>
                    </BreadcrumbList>
                </Breadcrumb>
            </div>
            <div>
                <TableComponent
                    items={totalItems}
                    topActions={
                        <div>
                            <button
                                onClick={() => navigate('/agent_detail/create')}
                                className={`flex btn btn-sm`}>
                                + Nuevo
                            </button>
                        </div>}
                    pagination
                    search
                    header={[
                        {
                            type: KeyHeader.KEY,
                            label: 'Nombre',
                            key: 'name',
                        },
                        {
                            type: KeyHeader.VALUE,
                            label: 'Modelo',
                            toValue: (item) => {
                                const imgs = imgSource(item.getSelectModel().value);
                                if(imgs) {
                                    return (
                                        <div className="flex gap-10 items-center">
                                            <div className="flex flex-col gap-2">
                                                {
                                                    imgs.map((img, index) => (
                                                        <img key={index} width={70} src={img} alt={item.getSelectModel().value} />
                                                    ))
                                                }
                                            </div>
                                            <p>{item.getSelectModel().value}</p>
                                        </div>
                                    )
                                }

                                return (
                                    <p>{item.getSelectModel().value}</p>
                                )
                            },
                        },
                        {
                            type: KeyHeader.VALUE,
                            label: 'VectorStore',
                            toValue: (item) => {

                                if (item.getSelectVectorStore().value === 'Pinecone') {

                                    return (
                                        <div className="flex gap-4 items-center">
                                            <Database />
                                            <p>VectorStore Database</p>
                                        </div>
                                    );

                                } else {
                                    const img = imgVectorStore(item.getSelectVectorStore().value);
                                    if(img) {
                                        return (
                                            <div className="flex gap-10 items-center">
                                                <img width={70} src={img} alt={item.getSelectVectorStore().value} />
                                                <p>{item.getSelectVectorStore().value}</p>
                                            </div>
                                        )
                                    }

                                    return (
                                        <p>{item.getSelectVectorStore().value}</p>
                                    )
                                }


                            },
                        },
                        {
                            type: KeyHeader.VALUE,
                            label: 'Creación',
                            toValue: (item) => {
                                if(item.created_at) {
                                    const utcDate = !item.created_at.includes('Z') ? item.created_at + 'Z' : item.created_at;
                                    return new DateUtil(new Date(utcDate), 'yyyy-MM-dd hh:mm:ss a').getDate()
                                }
                                return new DateUtil(new Date(), 'yyyy-MM-dd hh:mm:ss a').getDate()
                            },
                        },
                        {
                            type: KeyHeader.VALUE,
                            label: 'Actualización',
                            toValue: (item) => {
                                if(item.updated_at) {
                                    const utcDate = !item.updated_at.includes('Z') ? item.updated_at + 'Z' : item.updated_at;
                                    return new DateUtil(new Date(utcDate), 'yyyy-MM-dd hh:mm:ss a').getDate()
                                }
                                return new DateUtil(new Date(), 'yyyy-MM-dd hh:mm:ss a').getDate()
                            },
                        },
                    ]}
                    actions={[
                        {
                            label: 'Duplicar',
                            icon: mdiContentCopy,
                            className: 'btn btn-sm',
                            onClick: () => {},
                        },
                        {
                            label: 'Editar',
                            icon: <PenBoxIcon className="icon-size" />,
                            className: 'btn btn-sm',
                            visible: (item) => {
                                return validateAgentByPermissionType({agent: item, permissionType: PermissionType.EDIT})
                            },
                            onClick: (item) => navigate(`/agent_detail/${item.id}`),
                        },
                        {
                            label: 'Eliminar',
                            visible: (item) => {
                                return validateAgentByPermissionType({agent: item, permissionType: PermissionType.DELETE})
                            },
                            icon: <TrashIcon className="icon-size" />,
                            className: `btn btn-danger`,
                            onClick: () => {},
                        },
                    ]}
                />
            </div>
        </div>
    );
};
